import { z } from "zod"

import {
  identityIdSchema,
  platformContextIdSchema,
  platformOperatorIdSchema,
  platformSessionIdSchema,
} from "./identifiers.js"

const timestampSchema = z.string().datetime({ offset: true })

export const platformAuthenticationMethodV1Schema = z.enum([
  "password",
  "mfa",
  "totp",
  "webauthn",
  "duo",
])

export const platformRiskLevelV1Schema = z.enum([
  "trusted",
  "standard",
  "elevated",
  "blocked",
])

export const platformRiskSignalV1Schema = z.enum([
  "new_device",
  "network_change",
  "low_reputation",
  "impossible_travel",
  "device_noncompliant",
])

export const platformRiskAssessmentV1Schema = z
  .object({
    level: platformRiskLevelV1Schema,
    signals: z.array(platformRiskSignalV1Schema).max(5),
    assessedAt: timestampSchema,
  })
  .strict()
  .superRefine((assessment, context) => {
    if (new Set(assessment.signals).size !== assessment.signals.length) {
      context.addIssue({
        code: "custom",
        path: ["signals"],
        message: "Platform risk signals must be unique.",
      })
    }
  })

export const platformSessionSummaryV1Schema = z
  .object({
    contractVersion: z.literal(1),
    contextKind: z.literal("platform"),
    platformSessionId: platformSessionIdSchema,
    platformContextId: platformContextIdSchema,
    platformOperatorId: platformOperatorIdSchema,
    identityId: identityIdSchema,
    deviceLabel: z.string().trim().min(1).max(128),
    authenticationMethods: z.array(platformAuthenticationMethodV1Schema).min(2).max(4),
    riskLevel: z.enum(["trusted", "standard", "elevated"]),
    authenticatedAt: timestampSchema,
    recentAuthenticationExpiresAt: timestampSchema,
    expiresAt: timestampSchema,
    lastSeenAt: timestampSchema,
    revokedAt: timestampSchema.nullable(),
  })
  .strict()
  .superRefine((session, context) => {
    const methods = new Set(session.authenticationMethods)
    if (methods.size !== session.authenticationMethods.length) {
      context.addIssue({
        code: "custom",
        path: ["authenticationMethods"],
        message: "Platform authentication methods must be unique.",
      })
    }
    if (!methods.has("password")
      || !["mfa", "totp", "webauthn", "duo"].some((method) => methods.has(
        method as z.infer<typeof platformAuthenticationMethodV1Schema>,
      ))) {
      context.addIssue({
        code: "custom",
        path: ["authenticationMethods"],
        message: "Platform sessions require password and MFA.",
      })
    }
    const authenticatedAt = Date.parse(session.authenticatedAt)
    if (Date.parse(session.recentAuthenticationExpiresAt) <= authenticatedAt
      || Date.parse(session.expiresAt) <= authenticatedAt
      || Date.parse(session.lastSeenAt) < authenticatedAt) {
      context.addIssue({
        code: "custom",
        message: "Platform session timestamps are invalid.",
      })
    }
  })

export const platformSessionListV1Schema = z
  .object({
    contractVersion: z.literal(1),
    sessions: z.array(platformSessionSummaryV1Schema).max(100),
  })
  .strict()

export const platformSessionCookieV1Schema = z
  .object({
    name: z.literal("__Host-impetusos-platform"),
    httpOnly: z.literal(true),
    secure: z.literal(true),
    sameSite: z.literal("strict"),
    path: z.literal("/"),
    domain: z.null(),
    maximumAgeSeconds: z.number().int().min(60).max(28_800),
  })
  .strict()

export const platformRecentAuthenticationDecisionV1Schema = z
  .object({
    contractVersion: z.literal(1),
    decision: z.enum(["allowed", "step_up_required"]),
    maximumAgeSeconds: z.number().int().min(60).max(900),
  })
  .strict()

export type PlatformAuthenticationMethodV1 = z.infer<
  typeof platformAuthenticationMethodV1Schema
>
export type PlatformRiskAssessmentV1 = z.infer<
  typeof platformRiskAssessmentV1Schema
>
export type PlatformRiskLevelV1 = z.infer<typeof platformRiskLevelV1Schema>
export type PlatformRecentAuthenticationDecisionV1 = z.infer<
  typeof platformRecentAuthenticationDecisionV1Schema
>
export type PlatformSessionCookieV1 = z.infer<
  typeof platformSessionCookieV1Schema
>
export type PlatformSessionListV1 = z.infer<typeof platformSessionListV1Schema>
export type PlatformSessionSummaryV1 = z.infer<
  typeof platformSessionSummaryV1Schema
>
